import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';

function parseRpcResult(data) {
  if (data == null) return null;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function toDateInputValue(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function TenantNew() {
  const navigate = useNavigate();
  const { id: editTenantId } = useParams();
  const isEdit = Boolean(editTenantId);

  const [propertyOptions, setPropertyOptions] = React.useState([]);
  const [propertiesLoading, setPropertiesLoading] = React.useState(true);
  const [propertiesError, setPropertiesError] = React.useState('');

  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [rentStatus, setRentStatus] = React.useState('paid');
  const [moveInDate, setMoveInDate] = React.useState('');
  const [moveOutDate, setMoveOutDate] = React.useState('');
  const [propertyId, setPropertyId] = React.useState('');
  const [roomLabel, setRoomLabel] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [loadInitial, setLoadInitial] = React.useState(() => Boolean(editTenantId));
  const [loadError, setLoadError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) {
        setPropertiesError(
          'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
        );
        setPropertyOptions([]);
        setPropertiesLoading(false);
        return;
      }
      setPropertiesLoading(true);
      setPropertiesError('');
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, location')
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        setPropertiesError(error.message);
        setPropertyOptions([]);
      } else {
        setPropertyOptions(data || []);
      }
      setPropertiesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isEdit || !editTenantId) {
      setLoadInitial(false);
      setLoadError('');
      return undefined;
    }

    if (!supabase) {
      setLoadInitial(false);
      setLoadError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadInitial(true);
      setLoadError('');
      const { data: row, error } = await supabase
        .from('prospective_tenants')
        .select(
          'id, full_name, email, phone, rent_status, move_in_date, move_out_date, property_id, room_label, created_at, updated_at'
        )
        .eq('id', editTenantId)
        .single();

      if (cancelled) return;

      if (error) {
        setLoadError(error.message);
        setLoadInitial(false);
        return;
      }

      setFullName(row.full_name || '');
      setEmail(row.email || '');
      setPhone(row.phone || '');
      setRentStatus(row.rent_status || 'pending');
      setMoveInDate(toDateInputValue(row.move_in_date));
      setMoveOutDate(toDateInputValue(row.move_out_date));
      setPropertyId(row.property_id || '');
      setRoomLabel(row.room_label || '');
      setPassword('');
      setConfirmPassword('');
      setLoadInitial(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editTenantId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      setSaveError('Please enter a full name.');
      return;
    }

    if (!isEdit) {
      if (!password.trim()) {
        setSaveError('Please enter a password (at least 6 characters).');
        return;
      }
      if (password.length < 6) {
        setSaveError('Password must be at least 6 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setSaveError('Password and confirmation do not match.');
        return;
      }
    } else if (password.trim() || confirmPassword.trim()) {
      if (password.length < 6) {
        setSaveError('New password must be at least 6 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setSaveError('Password and confirmation do not match.');
        return;
      }
    }

    setSaving(true);
    try {
      const moveIn = moveInDate ? moveInDate : null;
      const moveOut = moveOutDate ? moveOutDate : null;
      const propId = propertyId || null;

      if (isEdit) {
        const { data: rpcData, error: rpcError } = await supabase.rpc('update_prospective_tenant', {
          p_id: editTenantId,
          p_full_name: trimmedName,
          p_email: email.trim(),
          p_phone: phone.trim(),
          p_rent_status: rentStatus,
          p_move_in_date: moveIn,
          p_move_out_date: moveOut,
          p_property_id: propId,
          p_room_label: roomLabel.trim(),
          p_password: password.trim(),
        });
        if (rpcError) throw rpcError;
        const parsed = parseRpcResult(rpcData);
        if (!parsed?.ok) {
          setSaveError(parsed?.error || 'Could not update tenant.');
          return;
        }
      } else {
        const { data: rpcData, error: rpcError } = await supabase.rpc('create_prospective_tenant', {
          p_full_name: trimmedName,
          p_email: email.trim(),
          p_phone: phone.trim(),
          p_rent_status: rentStatus,
          p_move_in_date: moveIn,
          p_move_out_date: moveOut,
          p_property_id: propId,
          p_room_label: roomLabel.trim(),
          p_password: password,
        });
        if (rpcError) throw rpcError;
        const parsed = parseRpcResult(rpcData);
        if (!parsed?.ok) {
          setSaveError(parsed?.error || 'Could not create tenant.');
          return;
        }
      }
      navigate('/tenants');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSaveError(
        err?.message ||
          err?.error_description ||
          'Could not save tenant. Run sql/add_tenant_supabase.sql and sql/add_prospective_tenant_password_supabase.sql in Supabase.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tenants-root">
      <UniversalHeader />

      <div className="tenants-shell">
        <header className="tenants-header">
          <div>
            <h1 className="tenants-title">
              {isEdit ? 'Edit prospective tenant' : 'Add prospective tenant'}
            </h1>
            <p className="tenants-subtitle">
              {isEdit
                ? 'Update contact details, property assignment, and rent status.'
                : 'Capture contact details, property assignment, and rent status.'}
            </p>
          </div>

          <div className="tenants-actions">
            <button
              type="button"
              className="btn-ghost tenants-btn"
              disabled={saving || loadInitial}
              onClick={() => navigate('/tenants')}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="tenant-new-form"
              className="btn-primary tenants-btn"
              disabled={saving || propertiesLoading || loadInitial}
            >
              {saving
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Save prospective tenant'}
            </button>
          </div>
        </header>

        {saveError ? (
          <p className="properties-save-error" role="alert">
            {saveError}
          </p>
        ) : null}
        {propertiesError ? (
          <p className="properties-save-error" role="alert">
            {propertiesError}
          </p>
        ) : null}
        {loadError ? (
          <p className="properties-save-error" role="alert">
            {loadError}{' '}
            <button
              type="button"
              className="btn-ghost tenants-btn"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/tenants')}
            >
              Back to tenants
            </button>
          </p>
        ) : null}

        <main className="tenants-layout">
          <section className="tenants-list">
            <div className="properties-detail-card">
              {isEdit && loadInitial ? (
                <p className="properties-list-empty" style={{ margin: '20px 0' }}>
                  Loading tenant…
                </p>
              ) : null}
              <form
                id="tenant-new-form"
                className="properties-add-form"
                onSubmit={handleSubmit}
                style={{
                  display: isEdit && (loadInitial || loadError) ? 'none' : undefined,
                }}
              >
                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-name">
                      Full name
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-name"
                        className="input-element"
                        type="text"
                        placeholder="e.g. Aisha Khan"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-email">
                      Email
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-email"
                        className="input-element"
                        type="email"
                        placeholder="tenant@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-password">
                      {isEdit ? 'New password (optional)' : 'Password'}
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-password"
                        className="input-element"
                        type="password"
                        autoComplete={isEdit ? 'new-password' : 'new-password'}
                        placeholder={isEdit ? 'Leave blank to keep current' : 'At least 6 characters'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required={!isEdit}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-password-confirm">
                      Confirm password
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-password-confirm"
                        className="input-element"
                        type="password"
                        autoComplete="new-password"
                        placeholder={isEdit ? 'Repeat new password' : 'Repeat password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required={!isEdit}
                      />
                    </div>
                  </div>
                </div>
                {isEdit ? (
                  <p className="tenant-property-hint" style={{ margin: '0 0 12px' }}>
                    Leave both password fields blank to keep the existing password.
                  </p>
                ) : null}

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-phone">
                      Phone
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-phone"
                        className="input-element"
                        type="tel"
                        placeholder="+1 555 000 0000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-status">
                      Rent status
                    </label>
                    <div className="input-shell">
                      <select
                        id="tenant-status"
                        className="input-element"
                        value={rentStatus}
                        onChange={(e) => setRentStatus(e.target.value)}
                      >
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-mid">
                      MID
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-mid"
                        className="input-element"
                        type="date"
                        aria-label="Move-in date"
                        value={moveInDate}
                        onChange={(e) => setMoveInDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-mod">
                      MOD
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-mod"
                        className="input-element"
                        type="date"
                        aria-label="Move-out date"
                        value={moveOutDate}
                        onChange={(e) => setMoveOutDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-property">
                      Property
                    </label>
                    <div className="input-shell">
                      <select
                        id="tenant-property"
                        className="input-element"
                        value={propertyId}
                        onChange={(e) => setPropertyId(e.target.value)}
                        disabled={propertiesLoading || !supabase}
                      >
                        <option value="">
                          {propertiesLoading
                            ? 'Loading properties…'
                            : 'Select a property…'}
                        </option>
                        {propertyOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.location ? ` — ${p.location}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!propertiesLoading &&
                    supabase &&
                    propertyOptions.length === 0 ? (
                      <p className="tenant-property-hint">
                        No properties yet. Add one under Properties first.
                      </p>
                    ) : null}
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="tenant-room">
                      Room
                    </label>
                    <div className="input-shell">
                      <input
                        id="tenant-room"
                        className="input-element"
                        type="text"
                        placeholder="e.g. 3B"
                        value={roomLabel}
                        onChange={(e) => setRoomLabel(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default TenantNew;
